import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import * as I from '@/icons/Icon';
import { useCreateApiKeyGate } from '@/features/settings/api-keys/use-create-api-key-gate';
import { ACCESS } from '@/features/access/access';
import { CommandRowIcon } from './CommandRowIcon';
import { CREATE_API_KEY_KEYWORDS, visibleQuickActions } from './quick-actions';
import { useQuickActionGates } from './use-quick-action-gates';

type Props = {
  /** Closes the palette after an action actually runs. */
  onRun: () => void;
};

export function QuickActions({ onRun }: Props) {
  const navigate = useNavigate();
  const gates = useQuickActionGates();
  const apiKeyGate = useCreateApiKeyGate();
  const actions = visibleQuickActions(gates);
  const showApiKey = gates.can(ACCESS.apiKeyCreate);

  if (actions.length === 0 && !showApiKey) return null;

  return (
    <CommandGroup heading="Quick actions">
      {showApiKey && <QuickActionRow
        label="Create API key"
        icon={I.Plus}
        keywords={CREATE_API_KEY_KEYWORDS}
        disabledReason={apiKeyGate.blockedReason ?? apiKeyGate.planLockedReason}
        isResolving={apiKeyGate.isResolving}
        onRun={() => {
          void navigate({ to: '/api-keys', search: { dialog: 'create' } });
          onRun();
        }}
      />}
      {actions.map((action) => (
        <QuickActionRow
          key={action.id}
          label={action.label}
          keywords={action.keywords}
          icon={action.icon}
          disabledReason={gates.isResolving ? null : action.disabledReason(gates)}
          isResolving={gates.isResolving}
          onRun={() => {
            action.run(navigate);
            onRun();
          }}
        />
      ))}
    </CommandGroup>
  );
}

type RowProps = {
  label: string;
  icon: ComponentType<{ size?: number }>;
  keywords?: string[];
  /** Non-null → disabled, with this copy as the hover/focus tooltip. */
  disabledReason: string | null;
  /** Gate still in flight — the row looks enabled but does nothing. */
  isResolving: boolean;
  onRun: () => void;
};

/**
 * One quick-action row. Role-blocked actions never get here (filtered out);
 * what remains is disabled with a tooltip for the paused / plan-locked reasons.
 */
function QuickActionRow({
  label,
  icon,
  keywords,
  disabledReason,
  isResolving,
  onRun,
}: RowProps) {
  const isDisabled = disabledReason != null;

  return (
    <CommandItem
      value={label}
      keywords={keywords}
      disabled={isDisabled}
      className={cn(
        isDisabled && 'cursor-not-allowed opacity-50 data-[disabled=true]:pointer-events-auto',
      )}
      onSelect={() => {
        if (isDisabled || isResolving) return;
        onRun();
      }}
    >
      <CommandRowIcon icon={icon} />
      <RowLabel disabledReason={disabledReason}>{label}</RowLabel>
    </CommandItem>
  );
}

function RowLabel({
  disabledReason,
  children,
}: {
  disabledReason: string | null;
  children: ReactNode;
}) {
  if (!disabledReason) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-fit">{children}</span>
        </TooltipTrigger>
        <TooltipContent side="right">{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
